import { useSubscription } from '@apollo/client';
import { css } from '@emotion/css';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import { Headline } from '@volumental/common';
import { toDataURL } from 'qrcode';
import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getJourneyIdFromUrl } from '@volumental/journey-manager';
import { type Gbswl, Population } from '../../../api/gql/graphql';
import { reconstructionEvents } from '../../../api/subscriptions/reconstruction-events';
import { uiConfig } from '../../../services/config';
import { useInputStore, useSessionStore, useViewStore } from '../../../store';
import { type ScanResult } from '../../../types/types';

const subTitle = css`
  color: ${uiConfig.styles.colors.black};
  font-family: ${uiConfig.styles.fontFamily};
  font-style: normal;
  font-weight: ${uiConfig.styles.fontWeights.regular};
  font-size: 16px;
  line-height: 21px;
  text-align: center;
  overflow-wrap: normal;
  margin-top: 10px;
`;

const qrCodeImage = css`
  width: 200px;
  border-radius: 16px;
`;

interface QrCodeProps {
  onScan: (scan: ScanResult, survey?: Gbswl) => void;
}

export function QrCode({ onScan }: QrCodeProps): JSX.Element {
  const jid = getJourneyIdFromUrl();
  const sideBarOpen = useViewStore((store) => store.sideBarOpen);
  const { callbackUrl } = useSessionStore((store) => store);
  const { previousPurchaseID, subject } = useInputStore((store) => store);
  const [session] = useState(Math.random().toString(36).slice(-6));
  const [qrcode, setQrcode] = useState('');
  const [invalidQr, setInvalidQr] = useState(false);

  const { t } = useTranslation();

  useSubscription(reconstructionEvents, {
    variables: { session, gender: Population.Unisex },
    onData: (d) => {
      const { data } = d.data;
      if (data?.reconstruction.__typename === 'Reconstruction') {
        const { scan } = data.reconstruction;
        onScan({
          type: scan.__typename!,
          id: scan.id,
          publicID: scan.publicID,
          measurement: scan.measurement!,
          createdAt: scan.createdAt,
          meshes: {
            leftFoot: scan.meshes?.leftFoot || '',
            rightFoot: scan.meshes?.rightFoot || '',
          },
          claimed: scan.claimed,
        });
      }
    },
  });

  useEffect(() => {
    const cb = new URL(callbackUrl);
    cb.searchParams.append('vol-sidebaropen', 'true');
    cb.searchParams.append('vol-session', session);
    if (subject) {
      cb.searchParams.append('vol-subject', subject);
    }
    if (previousPurchaseID) {
      cb.searchParams.append('vol-pp-id', previousPurchaseID);
    }
    if (jid) {
      cb.searchParams.append('vol-jid', jid);
    }
    const qrCodeUrl = cb.toString();
    setInvalidQr(false);
    setQrcode('');
    toDataURL(qrCodeUrl, (err, imageData) => {
      if (err) {
        setInvalidQr(true);
        return;
      }
      setQrcode(imageData);
    });
  }, [callbackUrl, session, previousPurchaseID]);

  useEffect(() => {
    if (sideBarOpen && qrcode) {
      usageTracker.track(UsageTrackerEvent.ShowQrCode);
    }
  }, [sideBarOpen, qrcode]);

  return (
    <>
      {Boolean(qrcode) && (
        <img className={qrCodeImage} src={qrcode} alt={t('imageAlt.qr')} />
      )}
      {Boolean(invalidQr) && (
        <>
          <Headline>{t('pleaseOpenMobile')}</Headline>
          <div className={subTitle}>{t('findYourBestSize')}</div>
        </>
      )}
    </>
  );
}
